import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

import { Card, Row, Col, List, Tag, Button, Input as AntInput, Tabs as AntdTabs, Empty, Tooltip, Typography, Space, Avatar, Badge } from 'antd';
import { PushpinOutlined, PushpinFilled, SearchOutlined, VideoCameraOutlined, PlayCircleOutlined, PictureOutlined, FolderOutlined, ArrowUpOutlined, ArrowDownOutlined, SaveOutlined, DragOutlined, InfoCircleOutlined, GlobalOutlined, TagsOutlined, TagOutlined, NotificationOutlined, PlusOutlined, StarOutlined } from '@ant-design/icons';

const { Text } = Typography;

const PinnedManager = ({
  initialItems = [],
  onSave,
  loading,
  fetchMovies,
  fetchSeries,
  fetchBanners,
  fetchAnnouncements,
  fetchSpotlights,
  // Opens the create-announcement modal. Absent -> the Add New button is
  // not rendered, so a caller that does not support creating still works.
  onCreateAnnouncement,
  // Opens the create-spotlight modal, same contract as onCreateAnnouncement.
  onCreateSpotlight,
  // Receives addToPinned so the parent can pin something it just created.
  registerPinItem,
  fetchCollections,
  // Optional fetchers for browse-only tabs
  fetchLanguages,
  fetchGenres,
  fetchTags,
  isActive = true,
  hasCategoryOrTags = false,
  setCopyPinned,
 
}) => {
  const [pinned, setPinned] = useState(initialItems || []);
  const [catalogTab, setCatalogTab] = useState('movie');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogRows, setCatalogRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const perPage = 20;
  const scrollRef = useRef(null);
  const requestIdRef = useRef(0);
 
  // Which field carries a pinned item's id, per type — the ONE place that knows.
  //
  // This used to be spelled out separately in four places: the pinnable list, the
  // React key, the already-pinned check and the save payload. Three of those fail
  // SILENTLY when a type is missing from them, and adding announcements and
  // spotlights proved it: the save fell through to a payload with no id, the API
  // stored a row whose foreign keys were all null, and the UI reported success
  // while the item vanished on reload. Deriving all four from this map means a new
  // type is one line, and forgetting it is impossible rather than invisible.
  const ID_FIELD_BY_TYPE = useMemo(() => ({
    movie: 'video_show_id',
    tv_show: 'tv_show_id',
    banner: 'banner_id',
    announcement: 'announcement_id',
    spotlight: 'spotlight_id',
    collection: 'collection_id',
    language: 'lang_id',
    genre: 'genre_id',
    tag: 'tag_id',
  }), []);

  // Types that can be pinned and saved
  const PINNABLE_TYPES = useMemo(() => Object.keys(ID_FIELD_BY_TYPE), [ID_FIELD_BY_TYPE]);

  // The id an item carries, whatever its type. Null when the type is unknown or
  // the field is absent, which every caller below treats as "cannot be pinned".
  const idOf = (item) => {
    const field = ID_FIELD_BY_TYPE[item?.type];
    if (!field) return null;
    const value = Number(item?.[field]);
    return Number.isFinite(value) && value > 0 ? value : null;
  };

  // Drag & Drop state (right pane)
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [pinnedSearch, setPinnedSearch] = useState('');
  const getItemKey = (it, idx) => it.key || `${it.type}-${idOf(it) ?? idx}`;

  // Helper functions for better UI
  const getTypeIcon = (type) => {
    switch (type) {
      case 'movie': return <VideoCameraOutlined  />;
      case 'tv_show': return <PlayCircleOutlined style={{ color: '#52c41a' }} />;
      case 'banner': return <PictureOutlined style={{ color: '#1890ff' }} />;
      case 'announcement': return <NotificationOutlined style={{ color: '#eb2f96' }} />;
      case 'spotlight': return <StarOutlined style={{ color: '#fa8c16' }} />;
      case 'collection': return <FolderOutlined style={{ color: '#722ed1' }} />;
      case 'language': return <GlobalOutlined style={{ color: '#faad14' }} />;
      case 'genre': return <TagsOutlined style={{ color: '#13c2c2' }} />;
      case 'tag': return <TagOutlined style={{ color: '#f5222d' }} />;
      default: return <InfoCircleOutlined />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'movie': return '#ff4d4f';
      case 'tv_show': return '#52c41a';
      case 'banner': return '#1890ff';
      case 'announcement': return '#eb2f96';
      case 'spotlight': return '#fa8c16';
      case 'collection': return '#722ed1';
      case 'language': return '#faad14';
      case 'genre': return '#13c2c2';
      case 'tag': return '#f5222d';
      default: return '#d9d9d9';
    }
  };

  const getTypeName = (type) => {
    switch (type) {
      case 'movie': return 'Movie';
      case 'tv_show': return 'TV Show';
      case 'banner': return 'Banner';
      case 'announcement': return 'Announcement';
      case 'spotlight': return 'Spotlight';
      case 'collection': return 'Collection';
      case 'language': return 'Language';
      case 'genre': return 'Genre';
      case 'tag': return 'Tag';
      default: return type;
    }
  };

  useEffect(() => {
    setPinned(initialItems || []);
  }, [initialItems]);

  const fetchMap = useMemo(() => ({
    movie: fetchMovies,
    tv_show: fetchSeries,
    banner: fetchBanners,
    announcement: fetchAnnouncements,
    spotlight: fetchSpotlights,
    collection: fetchCollections,
    // browse-only tabs
    language: fetchLanguages,
    genre: fetchGenres,
    tag: fetchTags,
  }), [fetchMovies, fetchSeries, fetchBanners, fetchAnnouncements, fetchSpotlights, fetchCollections, fetchLanguages, fetchGenres, fetchTags]);

  const fetchCatalog = async (nextPage = 1, append = false) => {
    try {
      setCatalogLoading(true);
      const fn = fetchMap[catalogTab];
      if (fn) {
        const myReq = ++requestIdRef.current;
        const res = await fn(catalogSearch, nextPage, perPage);
        if (myReq !== requestIdRef.current) return; // stale response, ignore
        const items = res?.items || res || [];
        // Ensure items have the correct type property and filter by current tab
        const typedItems = items.map(item => ({
          ...item,
          type: catalogTab // Ensure type matches the selected tab
        })).filter(item => item.type === catalogTab);
        
        const more = typeof res?.hasMore === 'boolean' ? res.hasMore : (Array.isArray(items) && items.length === perPage);
        setHasMore(!!more);
        setCatalogRows((prev) => (append ? [...prev, ...typedItems] : typedItems));

        setCopyPinned((prev) => (append ? [...prev, ...typedItems] : typedItems));
        if (catalogTab === 'movie' && typedItems.length > 0 && !append && nextPage === 1 && hasCategoryOrTags) {
          setPinned((prev) => {
            const existingIds = new Set(prev
              .filter(p => p.type === 'movie')
              .map(p => p.video_show_id));
            const newMovies = typedItems
              .filter(m => !existingIds.has(m.video_show_id))
              .map(m => ({ ...m, checked: true }));
            return [...prev, ...newMovies];
          });
        }
      }
    } finally {
      setCatalogLoading(false);
    }
  };

  // When parent activates the Pinned tab, default to Movies sub-tab and clear search
  useEffect(() => {
    if (isActive) {
      fetchCatalog(1, false);
      // Force default sub-tab to 'movie' each time tab becomes active
      if (catalogTab !== 'movie') setCatalogTab('movie');
      // Clear any previous search so default list is shown
      if (catalogSearch) setCatalogSearch('');
      // If nothing is loaded yet, reset pagination/scroll; fetch will be triggered by [catalogTab, catalogSearch] effect
      if (catalogRows.length === 0 && !catalogLoading) {
        setPage(1);
        setHasMore(true);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    // Reset list, page and scroll position on tab/search change
    setPage(1);
    setCatalogRows([]);
    setHasMore(true);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    fetchCatalog(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogTab, catalogSearch]);

  const onScroll = (e) => {
    const target = e.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100;
    if (nearBottom && !catalogLoading && hasMore) {
      const next = page + 1;
      setPage(next);
      fetchCatalog(next, true);
    }
  };

  // Helper function to check if an item is already pinned
  const isItemPinned = (item) => {
    const id = idOf(item);
    if (id === null) return false;
    return pinned.some((p) => p.type === item.type && idOf(p) === id);
  };

  const addToPinned = (item) => {
    if (!PINNABLE_TYPES.includes(item.type)) return;
    const exists = isItemPinned(item);
    if (!exists) setPinned((prev) => [...prev, { ...item, checked: true }]);
  };

  // Hand addToPinned up to the parent, so something created outside this
  // component — the Add New Announcement modal — can be pinned without the admin
  // going back to the catalog to find it. Registered once; the catalog refreshes
  // on its own when the tab is next opened.
  useEffect(() => {
    if (typeof registerPinItem !== 'function') return;
    registerPinItem(() => addToPinned);
    return () => registerPinItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerPinItem]);

  const removePinned = (index) => {
    setPinned((prev) => prev.filter((_, i) => i !== index));
  };

  const movePinned = (index, dir) => {
    setPinned((prev) => {
      const next = [...prev];
      const swapWith = dir === 'up' ? index - 1 : index + 1;
      if (swapWith < 0 || swapWith >= next.length) return prev;
      const temp = next[index];
      next[index] = next[swapWith];
      next[swapWith] = temp;
      return next;
    });
  };

  const handleSave = () => {
    const sortedItem = pinned
      // idOf null means the type is unknown or the id is missing. Such a row would
      // be stored with every foreign key null and read back as nothing, so it is
      // dropped here instead of being saved as an invisible ghost.
      .filter((it) => idOf(it) !== null)
      .map((it, index) => {
      // The id travels under the name its type uses, taken from the one map above
      // rather than an if-chain that could omit a type and post no id at all.
      return {
        type: it.type,
        order_no: index,
        [ID_FIELD_BY_TYPE[it.type]]: idOf(it),
        name: it.name,
      };
    });
    onSave && onSave(sortedItem);
  };

  return (
    <div style={{ maxWidth: 1200 }}>
      <Row gutter={[24, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            size="small"
            title={
              <Space>
                <SearchOutlined style={{ color: '#1890ff' }} />
                <span>Content Catalog</span>
                <Badge count={catalogRows.length} showZero color="#1890ff" />
              </Space>
            }
            bodyStyle={{ padding: '16px', display: 'flex', flexDirection: 'column', height: 'calc(600px - 57px)' }}
            style={{ height: '600px' }}
          >
            <AntdTabs
              activeKey={catalogTab}
              onChange={setCatalogTab}
              size="small"
              items={[
                { 
                  key: 'movie', 
                  label: (
                    <Space>
                      <VideoCameraOutlined  />
                      Movies
                    </Space>
                  )
                },
                { 
                  key: 'tv_show', 
                  label: (
                    <Space>
                      <PlayCircleOutlined style={{ color: '#52c41a' }} />
                      Series
                    </Space>
                  )
                },
                { 
                  key: 'banner', 
                  label: (
                    <Space>
                      <PictureOutlined style={{ color: '#1890ff' }} />
                      Banners
                    </Space>
                  )
                },
                {
                  key: 'announcement',
                  label: (
                    <Space>
                      <NotificationOutlined style={{ color: '#eb2f96' }} />
                      Announcements
                    </Space>
                  )
                },
                {
                  key: 'spotlight',
                  label: (
                    <Space>
                      <StarOutlined style={{ color: '#fa8c16' }} />
                      Spotlights
                    </Space>
                  )
                },
                {
                  key: 'collection',
                  label: (
                    <Space>
                      <FolderOutlined style={{ color: '#722ed1' }} />
                      Collections
                    </Space>
                  )
                },
                {
                  key: 'language',
                  label: (
                    <Space>
                      <GlobalOutlined style={{ color: '#faad14' }} />
                      Languages
                    </Space>
                  )
                },
                {
                  key: 'genre',
                  label: (
                    <Space>
                      <TagsOutlined style={{ color: '#13c2c2' }} />
                      Genres
                    </Space>
                  )
                },
                {
                  key: 'tag',
                  label: (
                    <Space>
                      <TagOutlined style={{ color: '#f5222d' }} />
                      Tags
                    </Space>
                  )
                },
              ]}
            />
            {/* Announcements are created here rather than on their own page: one
                is written for the rail it appears in, so making the admin leave,
                create it, come back and search for it is a round trip with no
                purpose. Creating it pins it straight away — same idea as the Add
                User modal inside Assign Subscription. */}
            {catalogTab === 'spotlight' && onCreateSpotlight && (
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={onCreateSpotlight}
                block
                style={{ marginBottom: 12 }}
              >
                Add New Spotlight
              </Button>
            )}
            {catalogTab === 'announcement' && onCreateAnnouncement && (
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={onCreateAnnouncement}
                block
                style={{ marginBottom: 12 }}
              >
                Add New Announcement
              </Button>
            )}
            <AntInput.Search
              placeholder={`Search ${getTypeName(catalogTab).toLowerCase()}s...`}
              allowClear
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
              onSearch={setCatalogSearch}
              style={{ marginBottom: 12 }}
              prefix={<SearchOutlined />}
            />
            <div
              ref={scrollRef}
              onScroll={onScroll}
              style={{ 
                height: '400px', // Fixed height
                overflow: 'auto', 
                border: '1px solid #f0f0f0', 
                borderRadius: 8,
                background: '#fafafa'
              }}
            >
              <List
                loading={catalogLoading && page === 1}
                locale={{ emptyText: <Empty description={`No ${getTypeName(catalogTab).toLowerCase()}s found`} /> }}
                dataSource={catalogRows}
                renderItem={(item, idx) => {
                  const isPinned = isItemPinned(item);
                  const canPin = PINNABLE_TYPES.includes(item.type);
                  return (
                    <div
                      key={item.key || `${item.type}-${idx}`}
                      draggable={!isPinned && canPin}
                      onDragStart={(e) => {
                        if (isPinned || !canPin) {
                          e.preventDefault();
                          return;
                        }
                        try {
                          // Ensure we are not in reorder mode when dragging from catalog
                          setDragIndex(null);
                          setHoverIndex(null);
                          e.dataTransfer.effectAllowed = 'copy';
                          const payload = JSON.stringify(item);
                          e.dataTransfer.setData('application/json', payload);
                          // Also set text/plain for better cross-browser support (e.g., Safari)
                          e.dataTransfer.setData('text/plain', payload);
                        } catch {}
                      }}
                      onDragEnd={() => { setHoverIndex(null); }}
                      style={{ padding: '8px' }}
                    >
                      <List.Item
                        actions={[
                          <Tooltip title={
                            !canPin
                              ? 'Pinning is not available for this type'
                              : (isPinned ? 'Already pinned' : `Pin this ${getTypeName(item.type).toLowerCase()}`)
                          } key="pin-tt">
                            <Button
                              key="pin"
                              size="small"
                              type="text"
                              icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />}
                              onClick={() => canPin && !isPinned && addToPinned(item)}
                              disabled={isPinned || !canPin}
                              style={{ 
                                color: isPinned ? '#52c41a' : getTypeColor(item.type), 
                                border: 'none', 
                                background: 'transparent',
                                opacity: isPinned ? 0.6 : 1
                              }}
                            />
                          </Tooltip>,
                        ]}
                        style={{ 
                          margin: 0, 
                          padding: '12px 16px', 
                          background: isPinned ? '#f6ffed' : '#fff',
                          border: `1px solid ${isPinned ? '#b7eb8f' : '#f0f0f0'}`, 
                          borderRadius: 8,
                          cursor: isPinned || !canPin ? 'default' : 'grab',
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                          opacity: isPinned ? 0.7 : 1
                        }}
                        className="catalog-item"
                      >
                        <List.Item.Meta 
                          avatar={
                            <Avatar 
                              icon={getTypeIcon(item.type)} 
                              style={{ backgroundColor: getTypeColor(item.type) + '20' }}
                            />
                          }
                          title={
                            <Text strong style={{ color: isPinned ? '#999' : '#262626' }}>
                              {item.name}
                            </Text>
                          }
                          description={
                            <Space>
                              <Tag 
                                color={getTypeColor(item.type)} 
                                style={{ borderRadius: 12, fontSize: '11px' }}
                              >
                                {getTypeName(item.type)}
                              </Tag>
                              {isPinned && (
                                <Tag color="success" style={{ borderRadius: 12, fontSize: '10px' }}>
                                  Pinned
                                </Tag>
                              )}
                            </Space>
                          }
                        />
                      </List.Item>
                    </div>
                  );
                }}
              />
              {catalogLoading && page > 1 && (
                <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>
                  <Text type="secondary">Loading more...</Text>
                </div>
              )}
              {!hasMore && catalogRows.length > 0 && (
                <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>
                  <Text type="secondary">No more results</Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            size="small"
            title={
              <Space>
                <PushpinFilled style={{ color: '#52c41a' }} />
                <span>Pinned Items</span>
                <Badge count={pinned.length} showZero color="#52c41a" />
              </Space>
            }
            extra={
              <Button 
                type="primary" 
                onClick={handleSave} 
                loading={loading}
                icon={<SaveOutlined />}
                size="small"
              >
                Save Order
              </Button>
            }
            bodyStyle={{ padding: '16px' }}
            style={{ height: '600px', display: 'flex', flexDirection: 'column' }}
          >
            <AntInput.Search 
              placeholder="Search pinned items..." 
              allowClear 
              style={{ marginBottom: 12 }}
              prefix={<SearchOutlined />}
              onChange={(e) => setPinnedSearch(e.target.value || '')}
            />
            {/* Accept drops from the left pane to pin items */}
            <div
              className="pinned-drop-container"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try { e.dataTransfer.dropEffect = 'copy'; } catch {}
                // Compute insertion index from cursor position relative to pinned items
                const container = e.currentTarget;
                const items = container.querySelectorAll('.pinned-item');
                const y = e.clientY;
                let insertion = pinned.length;
                for (let i = 0; i < items.length; i++) {
                  const rect = items[i].getBoundingClientRect();
                  const midpoint = rect.top + rect.height / 2;
                  if (y < midpoint) { insertion = i; break; }
                }
                setHoverIndex(insertion);

                // Auto-scroll when dragging near the top/bottom edges
                const scrollEdge = 40; // px threshold near edges
                const speed = 12; // px per event
                const cRect = container.getBoundingClientRect();
                const nearTop = y - cRect.top < scrollEdge;
                const nearBottom = cRect.bottom - y < scrollEdge;
                if (nearTop) {
                  container.scrollTop = Math.max(0, container.scrollTop - speed);
                } else if (nearBottom) {
                  container.scrollTop = Math.min(
                    container.scrollHeight - container.clientHeight,
                    container.scrollTop + speed
                  );
                }
              }}
              onDragLeave={(e) => {
                // When leaving the container entirely, clear hover
                const within = e.currentTarget.contains(e.relatedTarget);
                if (!within) setHoverIndex(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  // Handle reordering when dropping from pinned items onto container space
                  if (dragIndex !== null) {
                    setPinned((prev) => {
                      const next = [...prev];
                      const insertAt = (typeof hoverIndex === 'number' && hoverIndex >= 0 && hoverIndex <= next.length)
                        ? hoverIndex
                        : next.length;
                      const [moved] = next.splice(dragIndex, 1);
                      const target = insertAt > dragIndex ? insertAt - 1 : insertAt;
                      next.splice(target, 0, moved);
                      return next;
                    });
                    setDragIndex(null);
                    setHoverIndex(null);
                    return;
                  }
                  const text = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
                  if (!text) return;
                  const item = JSON.parse(text);
                  // Avoid duplicates
                  if (isItemPinned(item)) return;
                  setPinned((prev) => {
                    const next = [...prev];
                    const insertAt = (typeof hoverIndex === 'number' && hoverIndex >= 0 && hoverIndex <= next.length)
                      ? hoverIndex
                      : next.length;
                    next.splice(insertAt, 0, { ...item, checked: true });
                    return next;
                  });
                  setHoverIndex(null);
                } catch {}
              }}
              style={{ 
                height: '400px',
                overflow: 'auto',
                border: '2px dashed #d9d9d9',
                borderRadius: 8,
                background: '#fafafa',
                minHeight: '200px'
              }}
            >
              {pinned.length === 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '200px',
                  color: '#999'
                }}>
                  <DragOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                  <Text type="secondary">Drag items here to pin them</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Or click the pin button on catalog items
                  </Text>
                </div>
              )}
              {/* Top drop zone for inserting before the first item */}
              {pinned.length > 0 && (
                <div
                  className="pinned-top-dropzone"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(0); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (dragIndex !== null) {
                      setPinned((prev) => {
                        const next = [...prev];
                        const [moved] = next.splice(dragIndex, 1);
                        const target = 0; // top always 0 after removal
                        next.splice(target, 0, moved);
                        return next;
                      });
                      setDragIndex(null);
                      setHoverIndex(null);
                      return;
                    }
                    try {
                      const text = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
                      if (!text) return;
                      const item = JSON.parse(text);
                      if (isItemPinned(item)) return;
                      setPinned((prev) => [{ ...item, checked: true }, ...prev]);
                      setHoverIndex(null);
                    } catch {}
                  }}
                  style={{ height: 12, margin: '8px', borderTop: hoverIndex === 0 ? '2px solid #1890ff' : '2px solid transparent' }}
                />
              )}
              <List
                dataSource={pinned.filter((it) => {
                  const matchesSearch = (it.name || '').toLowerCase().includes(pinnedSearch.toLowerCase());
                  if (hasCategoryOrTags) {
                    return matchesSearch && it.type === 'movie';
                  }
                  return matchesSearch;
                })}
                locale={{ emptyText: null }}
                renderItem={(item, index) => (
                  <div
                    className="pinned-item"
                    key={getItemKey(item, index)}
                    draggable
                    onDragStart={(e) => {
                      setDragIndex(index);
                      try {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', String(index));
                      } catch {}
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = 'move';
                      // Compute precise insertion index based on cursor Y vs item midpoint
                      const rect = e.currentTarget.getBoundingClientRect();
                      const y = e.clientY;
                      const midpoint = rect.top + rect.height / 2;
                      const insertion = y < midpoint ? index : index + 1;
                      setHoverIndex(insertion);

                      // Also auto-scroll the parent container when hovering near edges
                      const container = e.currentTarget.closest('.pinned-drop-container');
                      if (container) {
                        const scrollEdge = 40;
                        const speed = 12;
                        const cRect = container.getBoundingClientRect();
                        const nearTop = y - cRect.top < scrollEdge;
                        const nearBottom = cRect.bottom - y < scrollEdge;
                        if (nearTop) {
                          container.scrollTop = Math.max(0, container.scrollTop - speed);
                        } else if (nearBottom) {
                          container.scrollTop = Math.min(
                            container.scrollHeight - container.clientHeight,
                            container.scrollTop + speed
                          );
                        }
                      }
                    }}
                    onDragEnter={() => setHoverIndex(index)}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Case 1: Reorder within pinned list
                      if (dragIndex !== null) {
                        const insertAt = (typeof hoverIndex === 'number') ? hoverIndex : index;
                        if (dragIndex === insertAt || dragIndex === insertAt - 1) return;
                        setPinned((prev) => {
                          const next = [...prev];
                          const [moved] = next.splice(dragIndex, 1);
                          const target = insertAt > dragIndex ? insertAt - 1 : insertAt;
                          next.splice(target, 0, moved);
                          return next;
                        });
                        setDragIndex(null);
                        setHoverIndex(null);
                        return;
                      }
                      // Case 2: Dropping from catalog onto a specific position
                      try {
                        const text = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
                        if (!text) return;
                        const item = JSON.parse(text);
                        if (isItemPinned(item)) return; // avoid duplicates
                        setPinned((prev) => {
                          const next = [...prev];
                          const insertAt = (typeof hoverIndex === 'number') ? Math.max(0, Math.min(hoverIndex, next.length)) : index;
                          next.splice(insertAt, 0, { ...item, checked: true });
                          return next;
                        });
                        setHoverIndex(null);
                      } catch {}
                    }}
                    onDragEnd={() => { setDragIndex(null); setHoverIndex(null); }}
                    style={{
                      border: '2px solid ' + ((hoverIndex === index || dragIndex === index) ? '#1890ff' : 'transparent'),
                      borderRadius: 8,
                      margin: '8px',
                      background: '#fff',
                      cursor: 'grab',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {hoverIndex === index && (
                      <div style={{ height: 0, borderTop: '2px solid #1890ff', marginBottom: 8 }} />
                    )}
                    {hoverIndex === index + 1 && (
                      <div style={{ height: 0, borderBottom: '2px solid #1890ff', marginTop: 8 }} />
                    )}
                    <List.Item
                      actions={[
                        <Tooltip title="Move up" key="up-tt">
                          <Button 
                            key="up" 
                            size="small" 
                            type="text"
                            icon={<ArrowUpOutlined />}
                            onClick={() => movePinned(index, 'up')}
                            disabled={index === 0}
                            style={{ border: 'none', background: 'transparent' }}
                          />
                        </Tooltip>,
                        <Tooltip title="Move down" key="down-tt">
                          <Button 
                            key="down" 
                            size="small" 
                            type="text"
                            icon={<ArrowDownOutlined />}
                            onClick={() => movePinned(index, 'down')}
                            disabled={index === pinned.length - 1}
                            style={{ border: 'none', background: 'transparent' }}
                          />
                        </Tooltip>,
                        <Tooltip title="Unpin this item" key="unpin-tt">
                          <Button 
                            key="remove" 
                            size="small" 
                            type="text" 
                            icon={<PushpinFilled />} 
                            onClick={() => removePinned(index)}
                            style={{ color: getTypeColor(item.type), border: 'none', background: 'transparent' }}
                          />
                        </Tooltip>,
                      ]}
                      style={{ margin: 0, padding: '12px 16px', border: 'none' }}
                    >
                      <List.Item.Meta 
                        avatar={
                          <Space>
                            <Text type="secondary" style={{ fontSize: '12px', minWidth: '20px' }}>
                              #{index + 1}
                            </Text>
                            <Avatar 
                              icon={getTypeIcon(item.type)} 
                              size="small"
                              style={{ backgroundColor: getTypeColor(item.type) + '20' }}
                            />
                          </Space>
                        }
                        title={
                          item.type === 'collection' ? (
                            <Link
                              to={`/collections/edit/${item.collection_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontWeight: 600, color: '#262626', textDecoration: 'none' }}
                            >
                              {item.name}
                            </Link>
                          ) : (
                            <Text strong style={{ color: '#262626' }}>
                              {item.name}
                            </Text>
                          )
                        }
                        description={
                          <Tag 
                            color={getTypeColor(item.type)} 
                            style={{ borderRadius: 12, fontSize: '11px' }}
                          >
                            {getTypeName(item.type)}
                          </Tag>
                        }
                      />
                    </List.Item>
                  </div>
                )}
              />
              {typeof hoverIndex === 'number' && hoverIndex === pinned.length && pinned.length > 0 && (
                <div style={{ height: 0, borderTop: '2px solid #1890ff', margin: '8px' }} />
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PinnedManager;
